import { HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, Subject, throwError } from 'rxjs';

import { EnjoyedYearDto } from '@app/data/dtos/ptrab/irs/enjoyed-year.dto';
import { IrsDto, IrsWithholdingDto } from '@app/data/dtos/ptrab/irs/irs.dto';
import {
  mapDataEnjoyedYear,
  mapDataIrs,
  mapDataIrsWithholding,
  mapDependantDto,
  mapSpouseDto,
  mapWorkerDto,
  mapYoungBenefitDto
} from '@app/data/mappers/ptrab/irs/irs.mappers';
import { IrsRepository } from '@app/domain/repositories/ptrab/irs/irs.repository.contract';
import { ErrorMsgEnum } from '@app/entities/enums/error/error-msg-enum';
import { IDataResponse } from '@app/entities/interfaces/data/data.interface';
import { TwoFactorAuthorization } from '@app/entities/interfaces/ptrab/two-factor-authorization.interface';
import { ErrorCodes, ErrorMsg } from '@app/entities/models/error/error.model';
import { EnjoyedYear } from '@app/entities/models/ptrab/irs/enjoyed-year.model';
import {
  Dependant,
  IrsModel,
  IrsWithholdingModel,
  Spouse,
  WorkerPt,
  YoungBenefit
} from '@app/entities/models/ptrab/irs/irs.model';
import { Logger } from '@app/services';
import { MSafeAny } from '@app/shared/models/safe-any/safe-any.model';
import { ENV } from '@environments/environment';
import { SECTION_KEY } from '@services/interceptors/error-sections.interceptor/error-sections.interceptor';

import { MRepository } from '@mercadona/core/utils/repository';
@Injectable({
  providedIn: 'root'
})
export class IrsRepositoryImpl extends MRepository implements IrsRepository {
  private logger = new Logger('IrsService');
  private onError = new Subject<ErrorMsgEnum>();
  errorMsg$ = new Subject<ErrorMsg>();
  onError$ = this.onError.asObservable();

  ptrabUrl = '/ptrab/irs';
  ptrabV2Url = '/ptrab/v2/irs';

  constructor() {
    super(`${ENV.API_URL_PTRAB}`);
  }

  getData(): Observable<IrsModel> {
    return this.get<IDataResponse<IrsDto>>(`${this.ptrabUrl}/personal-data`, {
      context: new HttpContext().set(SECTION_KEY, 'irs-data')
    }).pipe(
      map(mapDataIrs),
      catchError((error) => {
        const errorType =
          error && error.status === ErrorCodes.LOCKED ? ErrorMsgEnum.SERVICE_LOCKED : ErrorMsgEnum.SERVER_ERROR;

        this.onError.next(errorType);

        this.errorMsg$.next({ code: error.error.code, message: error.error.message });
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  modifyIrsPersonalData(worker: WorkerPt, authorization: TwoFactorAuthorization): Observable<IrsModel> {
    const params = mapWorkerDto(worker);
    const request = {
      body: {
        params,
        authorization
      }
    };
    return this.put<IDataResponse<IrsDto>>(`${this.ptrabUrl}/personal-data`, request).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.code, message: error.message });

        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  modifyIrsSpouse(spouse: Spouse, authorization: TwoFactorAuthorization): Observable<IrsModel> {
    const params = mapSpouseDto(spouse);
    const request = {
      body: {
        params,
        authorization
      }
    };
    return this.put<IDataResponse<IrsDto>>(`${this.ptrabUrl}/spouse`, request).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });

        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  modifyIrsDependent(dependant: Dependant, authorization: TwoFactorAuthorization): Observable<IrsModel> {
    const params = mapDependantDto(dependant);
    const request = {
      body: {
        params,
        authorization
      }
    };
    return this.put<IDataResponse<IrsDto>>(`${this.ptrabUrl}/dependent`, request).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });

        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  deleteIrsDependent(id: number): Observable<IrsModel> {
    return this.delete<IDataResponse<IrsDto>>(`${this.ptrabUrl}/dependent/${id}`).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });

        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  modifyIrsYoungBenefit(youngBenefit: YoungBenefit, authorization: TwoFactorAuthorization): Observable<IrsModel> {
    const params = mapYoungBenefitDto(youngBenefit);
    const request = {
      body: {
        params,
        authorization
      }
    };
    return this.put<IDataResponse<IrsDto>>(`${this.ptrabUrl}/irs-jovem`, request).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.code, message: error.message });

        this.logger.error(error);

        return throwError(() => error);
      })
    );
  }

  deleteIrsYoungBenefit(): Observable<IrsModel> {
    return this.delete<IDataResponse<IrsDto>>(`${this.ptrabUrl}/irs-jovem`).pipe(
      map(mapDataIrs),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  getWithholding(): Observable<IrsWithholdingModel> {
    return this.get<IDataResponse<IrsWithholdingDto>>(`${this.ptrabUrl}/withholding`, {
      context: new HttpContext().set(SECTION_KEY, 'irs-withholding')
    }).pipe(
      map(mapDataIrsWithholding),
      catchError((error) => {
        const errorType =
          error && error.status === ErrorCodes.LOCKED ? ErrorMsgEnum.SERVICE_LOCKED : ErrorMsgEnum.SERVER_ERROR;

        this.onError.next(errorType);

        this.errorMsg$.next({ code: error.error.code, message: error.error.message });
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  modifyIrsWithholding(
    withholding: IrsWithholdingModel,
    authorization: TwoFactorAuthorization
  ): Observable<IrsWithholdingModel> {
    const params = { voluntary_retention: withholding.voluntaryRetention };
    const request = {
      body: {
        params,
        authorization
      }
    };
    return this.put<IDataResponse<IrsWithholdingDto>>(`${this.ptrabUrl}/withholding`, request).pipe(
      map(mapDataIrsWithholding),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  deleteIrsWithholding(): Observable<IrsWithholdingModel> {
    return this.delete<IDataResponse<IrsWithholdingDto>>(`${this.ptrabUrl}/withholding`).pipe(
      map(mapDataIrsWithholding),
      catchError((error) => {
        this.errorMsg$.next({ code: error.error.code, message: error.error.message });
        this.logger.error(error);
        return throwError(() => error);
      })
    );
  }

  getEnjoyedYears(): Observable<EnjoyedYear[]> {
    return this.get<IDataResponse<EnjoyedYearDto[]>>(`${this.ptrabUrl}/enjoyed-years`).pipe(
      map(mapDataEnjoyedYear),
      catchError((error) => {
        const errorType =
          error && error.status === ErrorCodes.LOCKED ? ErrorMsgEnum.SERVICE_LOCKED : ErrorMsgEnum.SERVER_ERROR;
        this.onError.next(errorType);

        return of([] as MSafeAny);
      })
    );
  }

  getError() {
    return this.onError$;
  }

  getErrorMsg() {
    return this.errorMsg$;
  }
}
